import React, { useState, useEffect } from 'react';
import './AppTester.css';
import { monitoringAPI } from '../../../config/monitoring-api';
import { toast } from 'react-toastify';
import { X, Calendar, Clock, AlertCircle, AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, Play } from 'lucide-react';

const AppTester = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 20
  });

  const [filters, setFilters] = useState({
    dateFilter: 'today',
    startDate: '',
    endDate: ''
  });

  const [showCustomDate, setShowCustomDate] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchLogs = async (page = 1, dateFilter = 'today', startDate = '', endDate = '') => {
    try {
      setLoading(true);
      setError(null);

      const response = await monitoringAPI.getMonitoringLogs(page, 20, dateFilter, startDate, endDate);

      if (response.success) {
        setLogs(response.logs || []);
        setPagination(response.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          limit: 20
        });
      } else {
        setError(response.message || 'Failed to fetch monitoring logs');
        toast.error(response.message || 'Failed to fetch monitoring logs');
      }
    } catch (err) {
      console.error('Error in component fetching logs:', err);
      setError('An error occurred while fetching monitoring logs.');
      toast.error('An error occurred while fetching monitoring logs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(1, 'today', '', '');
  }, []);

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'dateFilter') {
      if (value === 'custom') {
        setShowCustomDate(true);
        setFilters(prev => ({ ...prev, dateFilter: value, startDate: '', endDate: '' }));
      } else {
        setShowCustomDate(false);
        setFilters(prev => ({ ...prev, dateFilter: value, startDate: '', endDate: '' }));
      }
    } else {
      setFilters(prev => ({ ...prev, [filterType]: value }));
    }
  };

  const applyFilters = () => {
    if (filters.dateFilter === 'custom') {
      if (!filters.startDate || !filters.endDate) {
        toast.error('Please select both start and end dates.');
        return;
      }

      const start = new Date(filters.startDate);
      const end = new Date(filters.endDate);
      if (start > end) {
        toast.error('Start date cannot be after end date.');
        return;
      }
    }

    setCurrentPage(1);
    fetchLogs(1, filters.dateFilter, filters.startDate, filters.endDate);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchLogs(newPage, filters.dateFilter, filters.startDate, filters.endDate);
    }
  };

  const handleCardClick = (log) => {
    setSelectedLog(log);
    setIsModalOpen(true);
  };

  const getStatusBadge = (log) => {
    const hasFailures = log.failedCount > 0 || (log.failed && log.failed.length > 0);
    const isCompleted = log.runStatus === 'completed';

    if (isCompleted && !hasFailures) {
      return (
        <span className="apptester-status-badge status-completed-success">
          <CheckCircle2 size={14} /> Completed
        </span>
      );
    } else if (isCompleted && hasFailures) {
      return (
        <span className="apptester-status-badge status-completed-issues">
          <AlertTriangle size={14} /> Issues
        </span>
      );
    } else if (log.runStatus === 'failed') {
      return (
        <span className="apptester-status-badge status-failed">
          <AlertCircle size={14} /> Failed
        </span>
      );
    } else {
      return (
        <span className="apptester-status-badge status-other">
          <Play size={14} /> {log.runStatus}
        </span>
      );
    }
  };

  return (
    <div className="apptester-page">
      <div className="apptester-page-header">
        <div className="apptester-page-title">
          <h1>App Tester Logs</h1>
          <p>Monitor end-to-end admin flows and system execution logs</p>
        </div>
      </div>

      <div className="apptester-filter-section">
        <div className="apptester-filter-form">
          <div className="apptester-filter-group">
            <label>Date Filter</label>
            <select
              value={filters.dateFilter}
              onChange={(e) => handleFilterChange('dateFilter', e.target.value)}
              className="apptester-filter-select"
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="custom">Custom Date</option>
            </select>
          </div>

          {showCustomDate && (
            <>
              <div className="apptester-filter-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="apptester-filter-input"
                />
              </div>

              <div className="apptester-filter-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="apptester-filter-input"
                />
              </div>
            </>
          )}

          <button onClick={applyFilters} className="apptester-apply-filters-button">
            Apply Filters
          </button>
        </div>
      </div>

      {loading ? (
        <div className="apptester-loading-state">
          <div className="apptester-loading-spinner"></div>
          <p className="apptester-loading-text">Loading app tester logs...</p>
        </div>
      ) : error ? (
        <div className="apptester-error-state">
          <p>{error}</p>
          <button
            onClick={() => fetchLogs(currentPage, filters.dateFilter, filters.startDate, filters.endDate)}
            className="apptester-retry-button"
          >
            Retry
          </button>
        </div>
      ) : logs.length === 0 ? (
        <div className="apptester-empty-state">
          <p>No monitoring logs found for the selected period.</p>
        </div>
      ) : (
        <>
          <div className="apptester-cards-grid">
            {logs.map((log) => {
              const hasFailures = log.failedCount > 0 || (log.failed && log.failed.length > 0);
              const failedStepsCount = log.failedCount || (log.failed ? log.failed.length : 0);
              const skippedStepsCount = (log.skipped ? log.skipped.length : 0);

              return (
                <div
                  key={log._id}
                  className="apptester-card"
                  onClick={() => handleCardClick(log)}
                >
                  <div className="apptester-card-header">
                    <div className="apptester-card-time">
                      <span className="apptester-time-label">Started At</span>
                      <span className="apptester-time-value">{log.startedAt || 'N/A'}</span>
                    </div>
                    {getStatusBadge(log)}
                  </div>

                  <div className="apptester-card-metrics">
                    <div className="apptester-metric-item">
                      <span className="apptester-metric-label">Passed</span>
                      <span className="apptester-metric-value val-passed">{log.passedCount || 0}</span>
                    </div>
                    <div className="apptester-metric-item">
                      <span className="apptester-metric-label">Failed</span>
                      <span className="apptester-metric-value val-failed">{failedStepsCount}</span>
                    </div>
                    <div className="apptester-metric-item">
                      <span className="apptester-metric-label">Skipped</span>
                      <span className="apptester-metric-value val-skipped">{skippedStepsCount}</span>
                    </div>
                  </div>

                  <div className="apptester-card-footer">
                    <span className="apptester-duration">
                      <Clock size={12} /> {log.duration || '0.0s'}
                    </span>
                    <span className="apptester-user-info">
                      {log.userId ? `User: ${log.userId}` : log.guestId ? `Guest: ${log.guestId}` : 'Anonymous'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {pagination.totalPages > 1 && (
            <div className="apptester-pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1 || loading}
                className="apptester-pagination-button"
              >
                <ChevronLeft size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                Previous
              </button>

              <span className="apptester-pagination-info">
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= pagination.totalPages || loading}
                className="apptester-pagination-button"
              >
                Next
                <ChevronRight size={16} style={{ verticalAlign: 'middle', marginLeft: '4px' }} />
              </button>
            </div>
          )}
        </>
      )}

      {isModalOpen && selectedLog && (
        <div className="apptester-modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="apptester-modal" onClick={(e) => e.stopPropagation()}>
            <div className="apptester-modal-header">
              <h3>App Tester Run Details</h3>
              <button className="apptester-modal-close" onClick={() => setIsModalOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="apptester-modal-body">
              <div className="apptester-details-meta">
                <div className="apptester-meta-field">
                  <span className="apptester-meta-label">Log ID</span>
                  <span className="apptester-meta-value mono">{selectedLog._id}</span>
                </div>
                <div className="apptester-meta-field">
                  <span className="apptester-meta-label">Status</span>
                  <span className="apptester-meta-value">{selectedLog.runStatus}</span>
                </div>
                <div className="apptester-meta-field">
                  <span className="apptester-meta-label">Started At</span>
                  <span className="apptester-meta-value">{selectedLog.startedAt || 'N/A'}</span>
                </div>
                <div className="apptester-meta-field">
                  <span className="apptester-meta-label">Completed At</span>
                  <span className="apptester-meta-value">{selectedLog.completedAt || 'N/A'}</span>
                </div>
                <div className="apptester-meta-field">
                  <span className="apptester-meta-label">Duration</span>
                  <span className="apptester-meta-value">{selectedLog.duration || '0s'}</span>
                </div>
                <div className="apptester-meta-field">
                  <span className="apptester-meta-label">Total Steps</span>
                  <span className="apptester-meta-value">{selectedLog.totalSteps || 0}</span>
                </div>
                <div className="apptester-meta-field">
                  <span className="apptester-meta-label">User ID</span>
                  <span className="apptester-meta-value mono">{selectedLog.userId || 'N/A'}</span>
                </div>
                <div className="apptester-meta-field">
                  <span className="apptester-meta-label">Guest ID</span>
                  <span className="apptester-meta-value mono">{selectedLog.guestId || 'N/A'}</span>
                </div>
              </div>

              <div>
                <h4 className="apptester-section-title">
                  <span style={{ color: '#ef4444' }}>●</span> Failed Steps ({selectedLog.failed ? selectedLog.failed.length : 0})
                </h4>
                {selectedLog.failed && selectedLog.failed.length > 0 ? (
                  <div className="apptester-steps-list">
                    {selectedLog.failed.map((step, idx) => {
                      const stepName = step.action || step.name || `Step ${idx + 1}`;
                      const durationStr = step.durationMs !== undefined ? ` (${step.durationMs}ms)` : '';
                      return (
                        <div key={idx} className="apptester-step-item step-failed">
                          <span className="apptester-step-name">{stepName}{durationStr}</span>
                          {step.error && (
                            <pre className="apptester-step-error-details">{step.error}</pre>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="apptester-no-steps">No failed steps in this run.</p>
                )}
              </div>

              <div>
                <h4 className="apptester-section-title">
                  <span style={{ color: '#f59e0b' }}>●</span> Skipped Steps ({selectedLog.skipped ? selectedLog.skipped.length : 0})
                </h4>
                {selectedLog.skipped && selectedLog.skipped.length > 0 ? (
                  <div className="apptester-steps-list">
                    {selectedLog.skipped.map((step, idx) => {
                      const stepName = step.action || step.name || (typeof step === 'string' ? step : `Step ${idx + 1}`);
                      const reasonStr = step.reason ? `: ${step.reason}` : '';
                      const durationStr = step.durationMs !== undefined && step.durationMs > 0 ? ` (${step.durationMs}ms)` : '';
                      return (
                        <div key={idx} className="apptester-step-item step-skipped">
                          <span className="apptester-step-name">
                            {stepName}{reasonStr}{durationStr}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="apptester-no-steps">No skipped steps in this run.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppTester;
